import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import { getLocalizedText, formatPrice } from '../utils/helpers';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { User, Settings, Award, BookOpen } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProfilePage() {
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated, getAuthHeaders, updateUser } = useAuthStore();
  const navigate = useNavigate();
  const [teacherProfile, setTeacherProfile] = useState(null);
  const [schoolProfile, setSchoolProfile] = useState(null);
  const [myServices, setMyServices] = useState([]);
  const [myCourses, setMyCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isTeacher, setIsTeacher] = useState(false);
  const [isSchool, setIsSchool] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    const load = async () => {
      try {
        const profilesRes = await axios.get(`${API_URL}/api/auth/profiles`, { headers: getAuthHeaders() });
        const profiles = profilesRes.data;
        
        setIsTeacher(profiles.is_teacher);
        setIsSchool(profiles.is_school);
        
        if (profiles.teacher) setTeacherProfile(profiles.teacher);
        if (profiles.school) setSchoolProfile(profiles.school);
        
        if (profiles.is_teacher) {
          const servicesRes = await axios.get(`${API_URL}/api/services/me/list`, { headers: getAuthHeaders() });
          setMyServices(servicesRes.data);
        }
        
        if (profiles.is_school) {
          const coursesRes = await axios.get(`${API_URL}/api/courses/me/list`, { headers: getAuthHeaders() });
          setMyCourses(coursesRes.data);
        }
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    };
    load();
  }, [isAuthenticated, user, getAuthHeaders, navigate]);

  if (!isAuthenticated) return null;

  const hasAvatar = user?.avatar;
  const hasPendingAvatar = user?.avatar_status === 'pending';
  const canBecomeTeacher = !isTeacher;
  const canBecomeSchool = !isSchool;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 animate-fade-in" data-testid="profile-page">
      <h1 className="font-serif text-4xl font-bold text-primary mb-8">{t('profile.title')}</h1>

      <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-20 h-20 rounded-full bg-cream-dark flex items-center justify-center overflow-hidden" style={{ backgroundColor: '#F5EFE6' }}>
            {hasAvatar ? (
              <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" />
            ) : (
              <User className="h-10 w-10 text-gold" style={{ color: '#D4A574' }} />
            )}
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-semibold">{user?.name}</h2>
            <p className="text-secondary">{user?.email}</p>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {isTeacher && <Badge className="bg-purple-500 text-white">Profesional</Badge>}
              {isSchool && <Badge className="bg-blue-500 text-white">Escuela</Badge>}
              {!isTeacher && !isSchool && <Badge variant="outline">{user?.role}</Badge>}
              {hasPendingAvatar && (
                <Badge className="bg-yellow-500 text-white text-xs">Photo pending</Badge>
              )}
            </div>
          </div>
          <Link to="/profile/edit">
            <Button variant="outline" className="border-gold text-gold" style={{ borderColor: '#D4A574', color: '#D4A574' }} data-testid="edit-profile-btn">
              <Settings className="h-4 w-4 mr-2" /> {t('profile.editProfile')}
            </Button>
          </Link>
        </div>

        {(canBecomeTeacher || canBecomeSchool) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
            {canBecomeTeacher && (
              <Link to="/become-teacher">
                <Button variant="outline" className="w-full border-gold text-gold" style={{ borderColor: '#D4A574', color: '#D4A574' }} data-testid="become-teacher-btn">
                  <Award className="h-4 w-4 mr-2" /> {t('profile.becomeTeacher')}
                </Button>
              </Link>
            )}
            {canBecomeSchool && (
              <Link to="/become-school">
                <Button variant="outline" className="w-full border-gold text-gold" style={{ borderColor: '#D4A574', color: '#D4A574' }} data-testid="become-school-btn">
                  <BookOpen className="h-4 w-4 mr-2" /> {t('profile.becomeSchool')}
                </Button>
              </Link>
            )}
          </div>
        )}
      </div>

      {/* Teacher Section - Services */}
      {isTeacher && (
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-2">
              <Award className="h-5 w-5 text-purple-500" />
              <h3 className="font-serif text-xl font-semibold">{t('profile.myServices')}</h3>
              {teacherProfile && !teacherProfile.is_approved && (
                <Badge className="bg-yellow-500 text-white text-xs">Perfil pendiente</Badge>
              )}
            </div>
            <Link to="/create-service">
              <Button className="bg-gold text-white" style={{ backgroundColor: '#D4A574' }} data-testid="create-service-btn">
                {t('profile.createService')}
              </Button>
            </Link>
          </div>
          {myServices.length === 0 ? (
            <p className="text-secondary">{t('common.noResults')}</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {myServices.map((service) => (
                <div key={service.id} className="p-4 border rounded-lg">
                  <h4 className="font-semibold">{getLocalizedText(service.title, i18n.language)}</h4>
                  <p className="text-sm text-secondary">{formatPrice(service.price)}</p>
                  <Badge className={service.is_approved ? 'bg-green-500' : 'bg-yellow-500'} variant="secondary">
                    {service.is_approved ? t('common.approved') : t('common.pending')}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* School Section - Courses */}
      {isSchool && (
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-blue-500" />
              <h3 className="font-serif text-xl font-semibold">{t('profile.myCourses')}</h3>
              {schoolProfile && !schoolProfile.is_approved && (
                <Badge className="bg-yellow-500 text-white text-xs">Perfil pendiente</Badge>
              )}
            </div>
            <Link to="/create-course">
              <Button className="bg-gold text-white" style={{ backgroundColor: '#D4A574' }} data-testid="create-course-btn">
                {t('profile.createCourse')}
              </Button>
            </Link>
          </div>
          {myCourses.length === 0 ? (
            <p className="text-secondary">{t('common.noResults')}</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {myCourses.map((course) => (
                <div key={course.id} className={`p-4 border rounded-lg ${course.is_expired || !course.is_active ? 'opacity-60 bg-gray-50' : ''}`}>
                  <div className="flex justify-between items-start">
                    <h4 className="font-semibold">{getLocalizedText(course.title, i18n.language)}</h4>
                    {(course.is_expired || !course.is_active) && (
                      <Badge className="bg-gray-500 text-white text-xs">Finalizado</Badge>
                    )}
                  </div>
                  <p className="text-sm text-secondary">{formatPrice(course.price)}</p>
                  {course.start_date && (
                    <p className="text-xs text-secondary mt-1">
                      Inicio: {new Date(course.start_date).toLocaleDateString()}
                    </p>
                  )}
                  <div className="flex gap-2 mt-2">
                    {!course.is_expired && course.is_active && (
                      <Badge className={course.is_approved ? 'bg-green-500' : 'bg-yellow-500'} variant="secondary">
                        {course.is_approved ? t('common.approved') : t('common.pending')}
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
